<template>
  <v-app dark>
    <app-drawer-left ref="drawer" @toggle="drawerOpen = $event" />
    <v-app-bar flat height="65" fixed app>
      <v-btn
        icon
        class="ps-drawer-toggle-btn ml-1"
        @click.stop="$refs.drawer.toggleSidebar()"
      >
        <v-icon small>{{
          drawerOpen ? "fal fa-arrow-left" : "fal fa-arrow-right"
        }}</v-icon>
      </v-btn>

      <v-toolbar-title
        class="ml-3 page-header-title"
        v-if="$vuetify.breakpoint.mdAndUp"
        >{{ pageTitle }}</v-toolbar-title
      >
      <v-spacer />
      <!-- Language Dropdown -->
      <v-menu
        offset-y
        bottom
        right
        content-class="ps-language-menu"
        v-model="languageMenuOpen"
        @input="onLanguageMenuToggle"
      >
        <template v-slot:activator="{ attrs, on }">
          <v-btn
            depressed
            v-bind="attrs"
            v-on="on"
            class="ps-lang-dropdown-btn mr-3"
            :class="{ 'ps-lang-dropdown-btn--active': languageMenuOpen }"
          >
            <v-img
              v-if="currentLocaleFlag"
              :src="currentLocaleFlag"
              max-width="20"
              max-height="20"
              contain
              class="mr-2"
            />
            <v-icon v-else small class="mr-2">fal fa-globe</v-icon>
            <span class="text-capitalize mr-3">{{
              currentLocaleEnglishName
            }}</span>
            <v-icon
              small
              :class="{ 'chevron-rotate': languageMenuOpen }"
              class="chevron-icon ps-lang-dropdown-btn__chevron"
            >
              fal fa-chevron-down
            </v-icon>
          </v-btn>
        </template>
        <v-list dense class="ps-language-menu__list">
          <v-list-item
            v-for="(locale, index) in locales"
            :key="index"
            @click="changeLocale(locale.code)"
            :class="{ 'v-list-item--active': locale.code === $i18n.locale }"
          >
            <v-list-item-avatar size="20">
              <v-img :src="locale.flag" />
            </v-list-item-avatar>
            <v-list-item-title>{{ locale.name }}</v-list-item-title>
            <v-list-item-icon v-if="locale.code === $i18n.locale">
              <v-icon small color="primary">fal fa-check</v-icon>
            </v-list-item-icon>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-btn class="rounded-lg" fab depressed small @click="changeTheme">
        <v-icon small>fal fa-{{ darkMode === true ? "sun" : "moon" }}</v-icon>
      </v-btn>
    </v-app-bar>
    <v-main class="ps-page-padded ps-admin-page">
      <v-container fluid>
        <router-view></router-view>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import AppDrawerLeft from "@/components/layouts/drawerLeft";
import { mapGetters } from "vuex";
import { setUser, checkToken } from "@/controllers/auth";
import { myMixin } from "@/mixin/main";

export default {
  components: { AppDrawerLeft },
  mixins: [myMixin],
  data() {
    return {
      drawerOpen: true,
      drawer: true,
      darkMode: false,
      languageMenuOpen: false,
    };
  },

  created() {
    checkToken(this);
    const themes = localStorage.getItem("darkMode");
    if (themes) {
      if (themes.length === 4) {
        this.$vuetify.theme.dark = true;
      } else {
        this.$vuetify.theme.dark = false;
      }
    }
  },
  mounted() {
    setUser(this);
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      locales: "auth/locales",
    }),
    currentLocaleFlag() {
      const currentLocale = this.locales.find(
        (locale) => locale.code === this.$i18n.locale
      );
      return currentLocale ? currentLocale.flag : null;
    },
    currentLocaleEnglishName() {
      const currentLocale = this.locales.find(
        (locale) => locale.code === this.$i18n.locale
      );
      return currentLocale ? currentLocale.name : "";
    },
  },

  methods: {
    changeTheme() {
      this.darkMode = !this.darkMode;
      const value = !this.$vuetify.theme.dark;
      this.$vuetify.theme.dark = value;
      localStorage.setItem("darkMode", value);
    },
    changeLocale(code) {
      localStorage.setItem("locale", code);
      this.$i18n.locale = code;
      this.languageMenuOpen = false;
    },
    onLanguageMenuToggle(value) {
      this.languageMenuOpen = value;
    },
  },
};
</script>

<style scoped>
.page-header-title {
  font-size: 16px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.92);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.theme--light .page-header-title {
  color: #141021;
}

.chevron-icon {
  transition: transform 0.3s ease;
}

.chevron-rotate {
  transform: rotate(180deg);
}

.ps-drawer-toggle-btn {
  width: 32px !important;
  height: 32px !important;
  border: 1px solid #e2e8f0 !important;
  background: #ffffff;
  border-radius: 8px !important;
  color: #94a3b8;
  transition: color 0.15s ease, border-color 0.15s ease,
    background-color 0.15s ease;
}

.ps-drawer-toggle-btn:hover {
  color: #5b39c9 !important;
  border-color: #5b39c9 !important;
  background: #f3f0fc !important;
}

.theme--dark .ps-drawer-toggle-btn {
  background: transparent;
  border-color: #2f333a !important;
  color: rgba(226, 232, 240, 0.6);
}

.theme--dark .ps-drawer-toggle-btn:hover {
  color: #7c5cea !important;
  border-color: #7c5cea !important;
  background: rgba(91, 57, 201, 0.2) !important;
}

/* Same shell as the sale page's category dropdown (.sale-category-select
   ::v-deep .v-input__slot): cream fill, light border, purple on open. */
.ps-lang-dropdown-btn {
  height: 44px !important;
  min-height: 44px !important;
  background-color: #fbf8f1 !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 12px !important;
  box-shadow: none !important;
  color: #141021 !important;
  font-weight: 700;
  font-size: 14px;
  text-transform: none !important;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.ps-lang-dropdown-btn--active {
  background-color: #ffffff !important;
  border-color: #5b39c9 !important;
}

.ps-lang-dropdown-btn__chevron {
  color: #5b39c9 !important;
}

::v-deep .ps-language-menu {
  overflow: hidden;
  background: #ffffff !important;
  border: 1px solid #d9dee7;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12) !important;
}

.ps-language-menu__list {
  padding: 4px 0;
  background: #ffffff !important;
}

.ps-language-menu__list ::v-deep .v-list-item {
  min-height: 38px;
  color: #334155;
}

.ps-language-menu__list ::v-deep .v-list-item:hover,
.ps-language-menu__list ::v-deep .v-list-item--active {
  background: #f3f0fc;
  color: #5b39c9;
}

.theme--dark .ps-lang-dropdown-btn {
  background-color: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  color: #f1f5f9 !important;
}

.theme--dark .ps-lang-dropdown-btn__label {
  color: #f1f5f9 !important;
}

.theme--dark .ps-lang-dropdown-btn__chevron {
  color: #9a8cf0 !important;
}

.theme--dark .ps-lang-dropdown-btn--active,
.theme--dark .ps-lang-dropdown-btn:hover {
  background-color: #262638 !important;
  border-color: #7c5cea !important;
}

.theme--dark ::v-deep .ps-language-menu,
::v-deep .theme--dark.ps-language-menu {
  background: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.theme--dark .ps-language-menu__list,
.ps-language-menu__list.theme--dark {
  background: #1e1e2d !important;
}

.theme--dark .ps-language-menu__list ::v-deep .v-list-item,
.ps-language-menu__list.theme--dark ::v-deep .v-list-item {
  color: #e2e8f0 !important;
}

.theme--dark .ps-language-menu__list ::v-deep .v-list-item:hover,
.theme--dark .ps-language-menu__list ::v-deep .v-list-item--active,
.ps-language-menu__list.theme--dark ::v-deep .v-list-item:hover,
.ps-language-menu__list.theme--dark ::v-deep .v-list-item--active {
  background: rgba(154, 140, 240, 0.16) !important;
  color: #9a8cf0 !important;
}
</style>
